/**
 * Admin API Client.
 *
 * Most admin calls use the shared axiosInstance. The session guard can pass a
 * token directly to avoid a refresh-time race with axios interceptor setup.
 */

import { axiosInstance } from '@/shared/lib/axios';
import { logger } from '@/shared/utils/logger';
import {
  toAdminApiResult,
  type AdminApiResult,
  type AdminWriteResponse,
} from './adminApiResult';
import type {
  AdminSession,
  AdminPaginationParams,
  DashboardMetrics,
  DashboardActivity,
  PaginatedResponse,
  AdminProvider,
  AdminProviderDetail,
  AdminProviderHealthResponse,
  OnboardingHealthResult,
  ProviderActivityRange,
  ProviderActivityResponse,
  ProviderHealthListStatus,
  AdminShop,
  AdminSubscription,
  AdminPayment,
  AdminServiceCategory,
  AdminServiceCategoryDetail,
  AdminServiceTemplate,
  AdminServiceTemplateDetail,
  AdminReview,
  AdminImageModerationAsset,
  ImageModerationStatus,
  ImageUploadPurpose,
  UploadedImageOwnerType,
  AdminAuditLog,
  ProviderType,
} from '../types/admin.types';

/**
 * Generate a unique idempotency key for monetary/destructive operations.
 * Required by POST /admin/subscriptions/:id/credit, cancel, and /payments/:id/refund.
 */
const generateIdempotencyKey = (): string => crypto.randomUUID();

const ADMIN_BASE = '/api/v1/admin';

const adminWriteRequest = <T>(
  request: () => Promise<{ data: T | AdminWriteResponse<T> }>,
  fallbackMessage: string,
): Promise<AdminApiResult<T>> =>
  toAdminApiResult(request, fallbackMessage, error =>
    logger.error(fallbackMessage, error),
  );

// ============================================================================
// Write Operations
// ============================================================================

export const updateProviderProfile = async (
  providerId: string,
  data: Record<string, unknown>,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () => axiosInstance.patch(`${ADMIN_BASE}/providers/${providerId}`, data),
    'Failed to update provider',
  );

export const updateShopStatus = async (
  shopId: string,
  status: string,
  reason: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.patch(`${ADMIN_BASE}/shops/${shopId}/status`, {
        status,
        reason,
      }),
    'Failed to update shop status',
  );

export const creditSubscription = async (
  subscriptionId: string,
  days: number,
  reason: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post(
        `${ADMIN_BASE}/subscriptions/${subscriptionId}/credit`,
        { days, reason },
        { headers: { 'X-Idempotency-Key': generateIdempotencyKey() } },
      ),
    'Failed to credit subscription',
  );

export const cancelSubscription = async (
  subscriptionId: string,
  reason: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post(
        `${ADMIN_BASE}/subscriptions/${subscriptionId}/cancel`,
        { reason },
        { headers: { 'X-Idempotency-Key': generateIdempotencyKey() } },
      ),
    'Failed to cancel subscription',
  );

export const refundPayment = async (
  paymentId: string,
  reason: string,
  amount?: number,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post(
        `${ADMIN_BASE}/payments/${paymentId}/refund`,
        { reason, amount },
        { headers: { 'X-Idempotency-Key': generateIdempotencyKey() } },
      ),
    'Failed to refund payment',
  );

export const moderateReview = async (
  reviewId: string,
  action: 'hide' | 'unhide',
  reason: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post(`${ADMIN_BASE}/reviews/${reviewId}/moderate`, {
        action,
        reason,
      }),
    'Failed to moderate review',
  );

export const approveImageModerationAsset = async (
  assetId: string,
  reason: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post(`${ADMIN_BASE}/moderation/images/${assetId}/approve`, {
        reason,
      }),
    'Failed to approve image moderation asset',
  );

export const rejectImageModerationAsset = async (
  assetId: string,
  reason: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post(`${ADMIN_BASE}/moderation/images/${assetId}/reject`, {
        reason,
      }),
    'Failed to reject image moderation asset',
  );

// Session
export const getAdminSession = async (
  accessToken?: string,
): Promise<AdminSession> => {
  const response = await axiosInstance.get<AdminSession>(
    `${ADMIN_BASE}/session`,
    accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : undefined,
  );
  return response.data;
};

// Dashboard
export const getDashboardMetrics =
  async (): Promise<DashboardMetrics | null> => {
    try {
      const response = await axiosInstance.get<DashboardMetrics>(
        `${ADMIN_BASE}/dashboard/metrics`,
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch dashboard metrics', error);
      return null;
    }
  };

export const getDashboardActivity =
  async (): Promise<DashboardActivity | null> => {
    try {
      const response = await axiosInstance.get<DashboardActivity>(
        `${ADMIN_BASE}/dashboard/activity`,
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch dashboard activity', error);
      return null;
    }
  };

// Providers
export const getProviders = async (
  params: AdminPaginationParams & { role?: string; onboardingHealth?: string },
): Promise<PaginatedResponse<AdminProvider> | null> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<AdminProvider>>(
      `${ADMIN_BASE}/providers`,
      { params },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch providers', error);
    return null;
  }
};

export const getProviderHealth = async (
  params: AdminPaginationParams & { status?: ProviderHealthListStatus },
): Promise<AdminProviderHealthResponse | null> => {
  try {
    const response = await axiosInstance.get<AdminProviderHealthResponse>(
      `${ADMIN_BASE}/provider-health`,
      { params },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch provider health', error);
    return null;
  }
};

export const getProviderDetail = async (
  providerId: string,
): Promise<AdminProviderDetail | null> => {
  try {
    const response = await axiosInstance.get<AdminProviderDetail>(
      `${ADMIN_BASE}/providers/${providerId}`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch provider detail', error);
    return null;
  }
};

export const getProviderActivity = async (
  providerId: string,
  range: ProviderActivityRange,
): Promise<ProviderActivityResponse | null> => {
  try {
    const response = await axiosInstance.get<ProviderActivityResponse>(
      `${ADMIN_BASE}/providers/${providerId}/activity`,
      { params: { range } },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch provider activity', error);
    return null;
  }
};

export const getProviderOnboardingHealth = async (
  providerId: string,
): Promise<OnboardingHealthResult | null> => {
  try {
    const response = await axiosInstance.get<OnboardingHealthResult>(
      `${ADMIN_BASE}/providers/${providerId}/onboarding-health`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch onboarding health', error);
    return null;
  }
};

// Shops
export const getShops = async (
  params: AdminPaginationParams & { status?: string; setupStatus?: string },
): Promise<PaginatedResponse<AdminShop> | null> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<AdminShop>>(
      `${ADMIN_BASE}/shops`,
      { params },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch shops', error);
    return null;
  }
};

export const getShopDetail = async (
  shopId: string,
): Promise<unknown | null> => {
  try {
    const response = await axiosInstance.get(`${ADMIN_BASE}/shops/${shopId}`);
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch shop detail', error);
    return null;
  }
};

// Subscriptions
export const getSubscriptions = async (
  params: AdminPaginationParams & {
    status?: string;
    tier?: string;
    cancelAtPeriodEnd?: boolean;
  },
): Promise<PaginatedResponse<AdminSubscription> | null> => {
  try {
    const response = await axiosInstance.get<
      PaginatedResponse<AdminSubscription>
    >(`${ADMIN_BASE}/subscriptions`, { params });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch subscriptions', error);
    return null;
  }
};

// Payments
export const getPayments = async (
  params: AdminPaginationParams & {
    status?: string;
    shopId?: string;
    from?: string;
    to?: string;
  },
): Promise<PaginatedResponse<AdminPayment> | null> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<AdminPayment>>(
      `${ADMIN_BASE}/payments`,
      { params },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch payments', error);
    return null;
  }
};

export const getPaymentDetail = async (
  paymentId: string,
): Promise<unknown | null> => {
  try {
    const response = await axiosInstance.get(
      `${ADMIN_BASE}/payments/${paymentId}`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch payment detail', error);
    return null;
  }
};

// Reviews
export const getReviews = async (
  params: AdminPaginationParams & { isHidden?: boolean; moderated?: boolean },
): Promise<PaginatedResponse<AdminReview> | null> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<AdminReview>>(
      `${ADMIN_BASE}/reviews`,
      { params },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch reviews', error);
    return null;
  }
};

export const getImageModerationAssets = async (
  params: AdminPaginationParams & {
    status?: ImageModerationStatus;
    ownerType?: UploadedImageOwnerType;
    ownerId?: string;
    purpose?: ImageUploadPurpose;
  },
): Promise<PaginatedResponse<AdminImageModerationAsset> | null> => {
  try {
    const response = await axiosInstance.get<
      PaginatedResponse<AdminImageModerationAsset>
    >(`${ADMIN_BASE}/moderation/images`, { params });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch image moderation assets', error);
    return null;
  }
};

export const getImageModerationAssetPreview = async (
  assetId: string,
): Promise<Blob | null> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `${ADMIN_BASE}/moderation/images/${assetId}/preview`,
      { responseType: 'blob' },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch image moderation preview', error);
    return null;
  }
};

// Service Categories
export const getServiceCategories = async (): Promise<
  AdminServiceCategory[] | null
> => {
  try {
    const response = await axiosInstance.get<AdminServiceCategory[]>(
      `${ADMIN_BASE}/services/categories`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch service categories', error);
    return null;
  }
};

export const getServiceCategoryDetail = async (
  categoryId: string,
): Promise<AdminServiceCategoryDetail | null> => {
  try {
    const response = await axiosInstance.get<AdminServiceCategoryDetail>(
      `${ADMIN_BASE}/services/categories/${categoryId}`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch category detail', error);
    return null;
  }
};

export const updateServiceCategory = async (
  categoryId: string,
  data: {
    name?: string;
    description?: string;
    isActive?: boolean;
    providerTypeScopes?: ProviderType[];
  },
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.patch(
        `${ADMIN_BASE}/services/categories/${categoryId}`,
        data,
      ),
    'Failed to update category',
  );

export interface CreateServiceCategoryInput {
  name: string;
  description?: string;
  providerTypeScopes: ProviderType[];
}

export type CreateServiceCategoryResult = AdminApiResult<AdminServiceCategory>;

export const createServiceCategory = async (
  data: CreateServiceCategoryInput,
): Promise<CreateServiceCategoryResult> =>
  adminWriteRequest(
    () =>
      axiosInstance.post<AdminServiceCategory>(
        `${ADMIN_BASE}/services/categories`,
        data,
      ),
    'Failed to create category',
  );

export const updateCategoryImage = async (
  categoryId: string,
  file: File,
): Promise<AdminApiResult<unknown>> => {
  const formData = new FormData();
  formData.append('image', file);
  return adminWriteRequest(
    () =>
      axiosInstance.post(
        `${ADMIN_BASE}/services/categories/${categoryId}/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    'Failed to update category image',
  );
};

// Service Templates
export const getAllServiceTemplates = async (): Promise<
  | (AdminServiceTemplate & {
      category: { id: string; name: string };
      _count: { images: number; services: number };
    })[]
  | null
> => {
  try {
    const response = await axiosInstance.get(
      `${ADMIN_BASE}/services/templates`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch all templates', error);
    return null;
  }
};

export const getServiceTemplates = async (
  categoryId: string,
): Promise<AdminServiceTemplate[] | null> => {
  try {
    const response = await axiosInstance.get<AdminServiceTemplate[]>(
      `${ADMIN_BASE}/services/categories/${categoryId}/templates`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch templates', error);
    return null;
  }
};

export const getServiceTemplateDetail = async (
  templateId: string,
): Promise<AdminServiceTemplateDetail | null> => {
  try {
    const response = await axiosInstance.get<AdminServiceTemplateDetail>(
      `${ADMIN_BASE}/services/templates/${templateId}`,
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch template detail', error);
    return null;
  }
};

export const createServiceTemplate = async (
  categoryId: string,
  data: {
    name: string;
    description?: string;
    defaultPrice?: number;
    defaultDuration?: number;
    serviceKind?: string;
    tags?: string[];
    providerTypeScopes?: ProviderType[];
  },
): Promise<AdminApiResult<AdminServiceTemplate>> =>
  adminWriteRequest(
    () =>
      axiosInstance.post<AdminServiceTemplate>(
        `${ADMIN_BASE}/services/categories/${categoryId}/templates`,
        data,
      ),
    'Failed to create template',
  );

export const updateServiceTemplate = async (
  templateId: string,
  data: Record<string, unknown>,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.patch(
        `${ADMIN_BASE}/services/templates/${templateId}`,
        data,
      ),
    'Failed to update template',
  );

export const uploadTemplateImage = async (
  templateId: string,
  file: File,
): Promise<AdminApiResult<unknown>> => {
  const formData = new FormData();
  formData.append('image', file);
  return adminWriteRequest(
    () =>
      axiosInstance.post(
        `${ADMIN_BASE}/services/templates/${templateId}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    'Failed to upload template image',
  );
};

export const softDeleteTemplateImage = async (
  templateId: string,
  imageId: string,
): Promise<AdminApiResult<unknown>> =>
  adminWriteRequest(
    () =>
      axiosInstance.patch(
        `${ADMIN_BASE}/services/templates/${templateId}/images/${imageId}/deactivate`,
      ),
    'Failed to deactivate template image',
  );

export const replaceTemplateImage = async (
  templateId: string,
  imageId: string,
  file: File,
  mode: 'future_only' | 'replace_everywhere' = 'future_only',
): Promise<AdminApiResult<unknown>> => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('mode', mode);
  return adminWriteRequest(
    () =>
      axiosInstance.post(
        `${ADMIN_BASE}/services/templates/${templateId}/images/${imageId}/replace`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    'Failed to replace template image',
  );
};

// Audit Logs
export const getAuditLogs = async (
  params: AdminPaginationParams & {
    action?: string;
    targetType?: string;
  },
): Promise<PaginatedResponse<AdminAuditLog> | null> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<AdminAuditLog>>(
      `${ADMIN_BASE}/audit-logs`,
      { params },
    );
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch audit logs', error);
    return null;
  }
};
